import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Barcode, Plus, Minus, Trash, MagnifyingGlass, Calculator, ShoppingCart,
  Wallet, Receipt, X, User as UserIcon, PencilSimple,
  Money, CreditCard, Bank, Coins,
} from "@phosphor-icons/react";
import BarcodeScanner from "../components/BarcodeScanner";
import ChangeCalculator from "../components/ChangeCalculator";
import TicketPrint from "../components/TicketPrint";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function POS() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Todos");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [freeSaleOpen, setFreeSaleOpen] = useState(false);
  const [freeForm, setFreeForm] = useState({ name: "", price: "", qty: 1 });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paidAmount, setPaidAmount] = useState("");
  const [walletUsed, setWalletUsed] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [cashPart, setCashPart] = useState("");
  const [transferPart, setTransferPart] = useState("");
  const [showReceipt, setShowReceipt] = useState(null);
  const [printSale, setPrintSale] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/products"), api.get("/customers")]);
    setProducts(p.data);
    setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => ["Todos", ...new Set(products.map((p) => p.category))], [products]);
  const filtered = useMemo(() => {
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return products.filter((p) => {
      if (category !== "Todos" && p.category !== category) return false;
      if (tokens.length === 0) return true;
      const haystack = [
        p.name,
        p.category,
        p.barcode,
        p.description,
        p.supplier_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [products, category, query]);

  const total = useMemo(() => cart.reduce((acc, it) => acc + it.subtotal, 0), [cart]);

  const addToCart = (product) => {
    if (product.stock <= 0) { toast.error("Producto sin stock"); return; }
    setCart((curr) => {
      const ex = curr.find((c) => c.product_id === product.product_id);
      if (ex) {
        if (ex.qty + 1 > product.stock) { toast.error("Stock insuficiente"); return curr; }
        return curr.map((c) => c.product_id === product.product_id
          ? { ...c, qty: c.qty + 1, subtotal: (c.qty + 1) * c.price }
          : c);
      }
      return [...curr, {
        product_id: product.product_id,
        name: product.name,
        barcode: product.barcode || "",
        price: product.price_sell,
        qty: 1,
        subtotal: product.price_sell,
        stock: product.stock,
      }];
    });
  };

  const updateQty = (id, delta) => {
    setCart((curr) => curr.map((c) => {
      if (c.product_id !== id) return c;
      const newQty = c.qty + delta;
      if (newQty <= 0) return null;
      if (newQty > c.stock) { toast.error("Stock insuficiente"); return c; }
      return { ...c, qty: newQty, subtotal: newQty * c.price };
    }).filter(Boolean));
  };

  const removeItem = (id) => setCart((curr) => curr.filter((c) => c.product_id !== id));

  const addFreeSale = (e) => {
    e?.preventDefault();
    const price = parseFloat(freeForm.price);
    const qty = parseInt(freeForm.qty) || 1;
    if (!freeForm.name.trim() || !price || price <= 0) {
      toast.error("Completa nombre y precio");
      return;
    }
    const free_id = `free_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setCart((curr) => [...curr, {
      product_id: free_id,
      name: freeForm.name.trim(),
      barcode: "",
      price,
      qty,
      subtotal: price * qty,
      stock: 9999,
      free: true,
    }]);
    toast.success(`Agregado: ${freeForm.name}`);
    setFreeForm({ name: "", price: "", qty: 1 });
    setFreeSaleOpen(false);
  };

  const handleBarcodeDetected = async (code) => {
    setScannerOpen(false);
    try {
      const { data } = await api.get(`/products/barcode/${code}`);
      addToCart(data);
      toast.success(`Agregado: ${data.name}`);
    } catch {
      toast.error(`Sin producto con código ${code}`);
    }
  };

  const customerWalletBalance = selectedCustomer?.wallet_balance || 0;

  const isMixed = paymentMethod === "mixto_ef_tr";
  const mixedCash = parseFloat(cashPart) || 0;
  const mixedTransfer = parseFloat(transferPart) || 0;
  const mixedTotal = mixedCash + mixedTransfer;

  const checkout = async () => {
    if (cart.length === 0) { toast.error("Carrito vacío"); return; }
    let paid;
    if (isMixed) {
      paid = mixedTotal;
    } else {
      paid = parseFloat(paidAmount) || 0;
    }
    const wallet = walletUsed || 0;
    if (paid + wallet < total) { toast.error("Pago insuficiente"); return; }
    let payment_method;
    if (wallet >= total && paid === 0) {
      payment_method = wallet > customerWalletBalance ? "cuenta corriente" : "billetera";
    } else if (isMixed) {
      const parts = [];
      if (mixedCash > 0) parts.push(`efectivo $${mixedCash.toFixed(0)}`);
      if (mixedTransfer > 0) parts.push(`transf $${mixedTransfer.toFixed(0)}`);
      const base = `mixto (${parts.join(" + ")})`;
      payment_method = wallet > 0
        ? (wallet > customerWalletBalance ? `${base} + cuenta corriente` : `${base} + billetera`)
        : base;
    } else if (wallet > 0 && paid > 0) {
      payment_method = wallet > customerWalletBalance
        ? `mixto (${paymentMethod} + cuenta corriente)`
        : `mixto (${paymentMethod})`;
    } else {
      payment_method = paymentMethod;
    }
    try {
      const { data } = await api.post("/sales", {
        customer_id: selectedCustomer?.customer_id || null,
        items: cart.map((c) => ({
          product_id: c.product_id, name: c.name, barcode: c.barcode,
          price: c.price, qty: c.qty, subtotal: c.subtotal,
        })),
        paid_amount: paid,
        wallet_used: wallet,
        payment_method,
      });
      toast.success("Venta registrada");
      setShowReceipt(data);
      setCart([]); setPaidAmount(""); setWalletUsed(0); setSelectedCustomer(null); setPaymentMethod("efectivo");
      setCashPart(""); setTransferPart("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la venta");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Operación</p>
          <h1 className="text-3xl font-heading font-bold">Punto de Venta</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFreeSaleOpen(true)}
            data-testid="pos-open-free-sale"
            className="flex items-center gap-2 bg-brand-gradient text-black font-semibold px-4 py-2 rounded-md hover:opacity-90 shadow-cyan-glow"
          >
            <PencilSimple size={18} weight="bold" /> Venta libre
          </button>
          <button
            onClick={() => setScannerOpen(true)}
            data-testid="pos-open-scanner"
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-md hover:bg-white/10"
          >
            <Barcode size={18} /> Escanear
          </button>
          <button
            onClick={() => setCalcOpen(true)}
            data-testid="pos-open-calc"
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-md hover:bg-white/10"
          >
            <Calculator size={18} /> Vuelto
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Products */}
        <section className="lg:col-span-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre o código..."
                data-testid="pos-search"
                className="w-full bg-[#0F1115] border border-white/10 rounded-md pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-turquoise font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.length === 0 ? (
              <p className="col-span-full text-center text-sm text-zinc-500 py-12">No hay productos</p>
            ) : filtered.map((p) => (
              <button
                key={p.product_id}
                onClick={() => addToCart(p)}
                data-testid={`pos-product-${p.product_id}`}
                disabled={p.stock <= 0}
                title={p.name}
                className="glass-card rounded-lg p-4 text-left hover:border-turquoise/50 hover:shadow-cyan-glow disabled:opacity-40 transition group flex flex-col"
              >
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{p.category}</p>
                <p className="font-medium text-white mt-1 leading-snug text-sm flex-1 break-words">{p.name}</p>
                <div className="flex items-end justify-between mt-3">
                  <p className="text-lg font-mono font-medium text-turquoise">$ {fmt(p.price_sell)}</p>
                  <p className={`text-[10px] font-mono ${p.stock <= p.stock_min ? "text-amber-400" : "text-zinc-500"}`}>
                    {p.stock} u
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Cart */}
        <aside className="lg:col-span-4 lg:sticky lg:top-6 self-start">
          <div className="glass-card rounded-xl flex flex-col h-[calc(100vh-7rem)]">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="font-heading font-semibold flex items-center gap-2">
                <ShoppingCart size={18} /> Carrito
                {cart.length > 0 && <span className="text-xs text-turquoise font-mono">({cart.length})</span>}
              </h2>
              {cart.length > 0 && (
                <button onClick={() => setCart([])} className="text-xs text-red-400 hover:underline">Vaciar</button>
              )}
            </div>

            {/* Customer */}
            <div className="px-5 py-3 border-b border-white/5">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono flex items-center gap-1.5">
                <UserIcon size={12} /> Cliente
              </label>
              <select
                value={selectedCustomer?.customer_id || ""}
                onChange={(e) => {
                  const c = customers.find((x) => x.customer_id === e.target.value);
                  setSelectedCustomer(c || null);
                  setWalletUsed(0);
                }}
                data-testid="pos-customer-select"
                className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
              >
                <option value="">Consumidor Final</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name} {c.wallet_balance > 0 ? `· $${fmt(c.wallet_balance)}` : c.wallet_balance < 0 ? `· DEBE $${fmt(Math.abs(c.wallet_balance))}` : ""}
                  </option>
                ))}
              </select>
              {selectedCustomer && (
                <div className={`mt-2 rounded-md p-2 text-xs border ${customerWalletBalance < 0 ? "bg-red-500/5 border-red-500/20" : "bg-turquoise/5 border-turquoise/20"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`flex items-center gap-1 ${customerWalletBalance < 0 ? "text-red-400" : "text-turquoise"}`}>
                      <Wallet size={12} /> {customerWalletBalance < 0 ? "Deuda" : "Billetera"}
                    </span>
                    <span className={`font-mono ${customerWalletBalance < 0 ? "text-red-400" : ""}`}>
                      {customerWalletBalance < 0 ? "-" : ""}$ {fmt(Math.abs(customerWalletBalance))}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    <input
                      type="number"
                      value={walletUsed}
                      onChange={(e) => {
                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                        setWalletUsed(v);
                      }}
                      placeholder="Cargar a cuenta..."
                      data-testid="pos-wallet-input"
                      className="flex-1 bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs font-mono"
                    />
                    {total > 0 && (
                      <button
                        type="button"
                        onClick={() => { setWalletUsed(total); setPaidAmount(""); }}
                        data-testid="pos-charge-all-btn"
                        className="px-2 py-1 rounded-md text-[10px] uppercase tracking-wider font-mono bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 whitespace-nowrap"
                      >
                        Fiar todo
                      </button>
                    )}
                  </div>
                  {walletUsed > customerWalletBalance && customerWalletBalance >= 0 && (
                    <p className="text-[10px] text-amber-400 font-mono mt-1.5">
                      ⚠ Excede saldo a favor — el cliente quedará debiendo $ {fmt(walletUsed - Math.max(0, customerWalletBalance))}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-2 py-2">
              {cart.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-zinc-500">
                  Agregá productos para iniciar
                </div>
              ) : (
                <ul className="space-y-1">
                  {cart.map((it) => (
                    <li key={it.product_id} className="px-3 py-2 rounded-md hover:bg-white/[0.02] group">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium leading-tight">{it.name}</p>
                          {it.free && (
                            <span className="inline-block mt-1 text-[9px] tracking-wider uppercase font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                              Venta libre
                            </span>
                          )}
                        </div>
                        <button onClick={() => removeItem(it.product_id)} className="text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100">
                          <Trash size={14} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateQty(it.product_id, -1)} className="w-7 h-7 rounded bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid={`pos-dec-${it.product_id}`}>
                            <Minus size={12} />
                          </button>
                          <span className="font-mono text-sm w-7 text-center">{it.qty}</span>
                          <button onClick={() => updateQty(it.product_id, 1)} className="w-7 h-7 rounded bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid={`pos-inc-${it.product_id}`}>
                            <Plus size={12} />
                          </button>
                        </div>
                        <p className="font-mono text-sm text-turquoise">$ {fmt(it.subtotal)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-white/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Subtotal</span>
                <span className="font-mono text-sm">$ {fmt(total)}</span>
              </div>
              {walletUsed > 0 && (
                <div className="flex items-center justify-between text-turquoise">
                  <span className="text-xs uppercase tracking-wider font-mono">Billetera</span>
                  <span className="font-mono text-sm">- $ {fmt(walletUsed)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-mono">A pagar</span>
                <span className="font-mono text-2xl font-medium text-white tabular-nums" data-testid="pos-total">
                  $ {fmt(Math.max(0, total - walletUsed))}
                </span>
              </div>

              {/* Payment method selector */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1.5">Método de pago</p>
                <div className="grid grid-cols-5 gap-1.5" data-testid="pos-payment-method-group">
                  {[
                    { value: "efectivo", label: "Efectivo", Icon: Money },
                    { value: "transferencia", label: "Transf.", Icon: Bank },
                    { value: "debito", label: "Débito", Icon: CreditCard },
                    { value: "credito", label: "Crédito", Icon: CreditCard },
                    { value: "mixto_ef_tr", label: "Mixto", Icon: Coins },
                  ].map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setPaymentMethod(value)}
                      data-testid={`pos-pay-${value}`}
                      className={`flex flex-col items-center gap-1 py-2 rounded-md border text-[10px] font-mono uppercase tracking-wider transition ${
                        paymentMethod === value
                          ? "bg-turquoise/10 border-turquoise text-turquoise shadow-cyan-glow"
                          : "bg-white/[0.02] border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <Icon size={16} weight={paymentMethod === value ? "fill" : "regular"} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {isMixed ? (
                <div className="space-y-2" data-testid="pos-mixed-inputs">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Efectivo</label>
                    <input
                      type="number"
                      value={cashPart}
                      onChange={(e) => setCashPart(e.target.value)}
                      placeholder="0"
                      data-testid="pos-mixed-cash"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Transferencia</label>
                    <input
                      type="number"
                      value={transferPart}
                      onChange={(e) => setTransferPart(e.target.value)}
                      placeholder="0"
                      data-testid="pos-mixed-transfer"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div className={`text-[10px] font-mono px-2 py-1.5 rounded ${
                    mixedTotal + walletUsed >= total ? "bg-turquoise/5 text-turquoise" : "bg-red-500/5 text-red-400"
                  }`}>
                    Suma: $ {fmt(mixedTotal)} {mixedTotal + walletUsed >= total
                      ? mixedCash > 0 && mixedTotal + walletUsed > total
                        ? `— Vuelto en efectivo: $ ${fmt(mixedTotal + walletUsed - total)}`
                        : "— Coincide"
                      : `— Falta $ ${fmt(total - mixedTotal - walletUsed)}`}
                  </div>
                </div>
              ) : (
                <>
                  <input
                    type="number"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    placeholder={paymentMethod === "efectivo" ? "Monto recibido" : "Monto cobrado (auto)"}
                    data-testid="pos-paid-input"
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                  />
                  {paymentMethod === "efectivo" && paidAmount && (parseFloat(paidAmount) + walletUsed) >= total && (
                    <div className="bg-turquoise/5 border border-turquoise/20 rounded p-2 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Vuelto</p>
                      <p className="font-mono text-lg text-turquoise tabular-nums">
                        $ {fmt((parseFloat(paidAmount) || 0) + walletUsed - total)}
                      </p>
                    </div>
                  )}
                </>
              )}
              <button
                onClick={() => {
                  if (isMixed) {
                    checkout();
                    return;
                  }
                  // For non-cash payments, auto-fill exact amount before checkout
                  if (paymentMethod !== "efectivo" && !paidAmount) {
                    setPaidAmount(String(Math.max(0, total - walletUsed)));
                    setTimeout(checkout, 0);
                    return;
                  }
                  checkout();
                }}
                disabled={cart.length === 0}
                data-testid="pos-checkout-btn"
                className="w-full py-3.5 bg-brand-gradient text-black font-heading font-bold text-base rounded-md disabled:opacity-40 shadow-cyan-glow hover:opacity-90 transition"
              >
                Confirmar Venta
              </button>
            </div>
          </div>
        </aside>
      </div>

      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={handleBarcodeDetected} />
      <ChangeCalculator open={calcOpen} onClose={() => setCalcOpen(false)} total={Math.max(0, total - walletUsed)} onApply={(v) => setPaidAmount(String(v))} />

      {freeSaleOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="glass-card rounded-xl w-full max-w-md p-6" data-testid="free-sale-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <PencilSimple size={22} className="text-turquoise" /> Venta libre
              </h3>
              <button onClick={() => setFreeSaleOpen(false)}><X size={20} /></button>
            </div>
            <p className="text-xs text-zinc-500 mb-5 font-mono">
              Agrega un item al carrito sin necesidad de tenerlo en el inventario.
            </p>
            <form onSubmit={addFreeSale} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Descripción *</label>
                <input
                  autoFocus
                  required
                  value={freeForm.name}
                  onChange={(e) => setFreeForm({ ...freeForm, name: e.target.value })}
                  placeholder="Ej: Reparación de pantalla, Servicio técnico..."
                  data-testid="free-sale-name-input"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-turquoise"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Precio unitario *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={freeForm.price}
                    onChange={(e) => setFreeForm({ ...freeForm, price: e.target.value })}
                    placeholder="0.00"
                    data-testid="free-sale-price-input"
                    className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-turquoise"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={freeForm.qty}
                    onChange={(e) => setFreeForm({ ...freeForm, qty: e.target.value })}
                    data-testid="free-sale-qty-input"
                    className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-turquoise"
                  />
                </div>
              </div>
              <div className="bg-black/40 rounded-md px-4 py-3 flex items-center justify-between border border-white/5">
                <span className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Subtotal</span>
                <span className="font-mono text-xl text-turquoise tabular-nums">
                  $ {fmt((parseFloat(freeForm.price) || 0) * (parseInt(freeForm.qty) || 1))}
                </span>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setFreeSaleOpen(false)} className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-md text-sm">
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="free-sale-add-btn"
                  className="flex-1 bg-brand-gradient text-black font-semibold py-2.5 rounded-md hover:opacity-90"
                >
                  Agregar al carrito
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="glass-card rounded-xl w-full max-w-md p-6" data-testid="receipt-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <Receipt size={20} className="text-turquoise" /> Comprobante
              </h3>
              <button onClick={() => setShowReceipt(null)}><X size={20} /></button>
            </div>
            <div className="text-center mb-4">
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 font-mono">CRIYE</p>
              <p className="font-mono text-xs text-zinc-600 mt-1">{showReceipt.sale_id}</p>
            </div>
            <div className="border-y border-white/5 py-3 space-y-1">
              {showReceipt.items.map((it) => (
                <div key={`${showReceipt.sale_id}-${it.product_id}`} className="flex justify-between text-xs font-mono">
                  <span>{it.qty}× {it.name}</span>
                  <span>$ {fmt(it.subtotal)}</span>
                </div>
              ))}
            </div>
            <div className="py-3 space-y-1 text-xs font-mono">
              <div className="flex justify-between"><span className="text-zinc-500">Total</span><span>$ {fmt(showReceipt.total)}</span></div>
              {showReceipt.wallet_used > 0 && <div className="flex justify-between text-turquoise"><span>Billetera</span><span>$ {fmt(showReceipt.wallet_used)}</span></div>}
              <div className="flex justify-between"><span className="text-zinc-500">Recibido</span><span>$ {fmt(showReceipt.paid_amount)}</span></div>
              <div className="flex justify-between border-t border-white/5 pt-2 mt-2 text-turquoise text-base"><span>Vuelto</span><span>$ {fmt(showReceipt.change_amount)}</span></div>
            </div>
            <button onClick={() => setShowReceipt(null)} className="w-full bg-brand-gradient text-black font-semibold py-2 rounded-md mt-4">
              Cerrar
            </button>
            <button
              onClick={() => setPrintSale(showReceipt)}
              data-testid="pos-print-ticket-btn"
              className="w-full mt-2 py-2 rounded-md border border-turquoise/40 text-turquoise hover:bg-turquoise/10 font-semibold flex items-center justify-center gap-2"
            >
              <Receipt size={18} weight="duotone" /> Imprimir ticket
            </button>
          </div>
        </div>
      )}

      {printSale && <TicketPrint sale={printSale} onClose={() => setPrintSale(null)} />}
    </div>
  );
}
